import React, { useEffect, useMemo, useRef, useState } from 'react';
import HTMLFlipBook from 'react-pageflip';
import { X, LayoutGrid, Search, ZoomIn, Share2, ChevronLeft, ChevronRight, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const ZOOMS = [100, 150, 200];
const BASE_W = 460;

const Page = React.forwardRef(({ image, num, label }, ref) => (
  <div ref={ref} className="bg-white shadow-inner overflow-hidden" data-testid={`flip-page-${num}`}>
    {image ? (
      <img src={image} alt={`Page ${num}`} className="w-full h-full object-contain select-none" draggable={false} />
    ) : (
      <div className="w-full h-full flex flex-col items-center justify-center text-gray-300 gap-2">
        <Loader2 className="w-6 h-6 animate-spin" />
        <span className="text-xs font-mono">Page {num}</span>
      </div>
    )}
  </div>
));
Page.displayName = 'Page';

export const PdfFlipReader = ({ report, initialPage = 1, onClose }) => {
  const [doc, setDoc] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [images, setImages] = useState({});
  const [thumbs, setThumbs] = useState({});
  const [current, setCurrent] = useState(initialPage);
  const [zoom, setZoom] = useState(100);
  const [panel, setPanel] = useState(null); // 'thumbs' | 'search'
  const [query, setQuery] = useState('');
  const [activeQuery, setActiveQuery] = useState('');
  const [matches, setMatches] = useState(null);
  const [searching, setSearching] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [pageRatio, setPageRatio] = useState(1.414);
  const bookRef = useRef(null);
  const textCache = useRef({});
  const renderQueue = useRef(new Set());

  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}${report.pdf}`;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const task = pdfjsLib.getDocument({
          url: pdfUrl,
          disableAutoFetch: true,
          disableStream: true,
          rangeChunkSize: 1048576,
        });
        const d = await task.promise;
        if (cancelled) return;
        const p1 = await d.getPage(1);
        const vp = p1.getViewport({ scale: 1 });
        if (!cancelled) {
          setPageRatio(vp.height / vp.width);
          setDoc(d);
          setNumPages(d.numPages);
        }
      } catch (e) {
        if (!cancelled) setLoadError(String(e?.message || e));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfUrl]);

  const renderPage = async (num, scale = 1.6) => {
    if (!doc || images[num] || renderQueue.current.has(num) || num < 1 || num > doc.numPages) return;
    renderQueue.current.add(num);
    try {
      const page = await doc.getPage(num);
      const vp = page.getViewport({ scale });
      const canvas = document.createElement('canvas');
      canvas.width = vp.width;
      canvas.height = vp.height;
      await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise;
      const url = canvas.toDataURL('image/jpeg', 0.82);
      setImages((prev) => ({ ...prev, [num]: url }));
    } catch (e) { /* page render failed; placeholder remains */ }
  };

  // Render only pages near the current spread (images are heavy; text is pre-extracted separately)
  useEffect(() => {
    if (!doc) return;
    let stopped = false;
    (async () => {
      const around = [];
      for (let d = 0; d <= 8; d++) { around.push(current + d); if (d > 0) around.push(current - d); }
      for (const n of around) { if (!stopped) await renderPage(n); }
    })();
    return () => { stopped = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, current]);

  // Pre-extract text of all pages in the background so search is fast
  useEffect(() => {
    if (!doc) return;
    let stopped = false;
    (async () => {
      for (let n = 1; n <= doc.numPages; n++) {
        if (stopped) return;
        if (textCache.current[n] !== undefined) continue;
        try {
          const page = await doc.getPage(n);
          const tc = await page.getTextContent();
          textCache.current[n] = tc.items.map((i) => i.str).join(' ');
        } catch { textCache.current[n] = ''; }
      }
    })();
    return () => { stopped = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc]);

  // Thumbnails on demand
  useEffect(() => {
    if (!doc || panel !== 'thumbs') return;
    let stopped = false;
    (async () => {
      for (let n = 1; n <= doc.numPages; n++) {
        if (stopped) return;
        if (thumbs[n]) continue;
        try {
          const page = await doc.getPage(n);
          const vp = page.getViewport({ scale: 120 / page.getViewport({ scale: 1 }).width });
          const canvas = document.createElement('canvas');
          canvas.width = vp.width; canvas.height = vp.height;
          await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise;
          const url = canvas.toDataURL('image/jpeg', 0.6);
          setThumbs((prev) => ({ ...prev, [n]: url }));
        } catch (e) { /* skip */ }
      }
    })();
    return () => { stopped = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, panel]);

  useEffect(() => {
    window.history.replaceState(null, '', `#report=${report.id}&page=${current}`);
  }, [current, report.id]);

  const flipTo = (num) => {
    const book = bookRef.current?.pageFlip?.();
    if (book) book.flip(Math.max(0, Math.min(numPages - 1, num - 1)));
  };

  useEffect(() => {
    if (doc && initialPage > 1) {
      const t = setTimeout(() => flipTo(initialPage), 400);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc]);

  const runSearch = async (q) => {
    if (!doc || !q) return;
    setSearching(true);
    setActiveQuery(q);
    const found = [];
    const lower = q.toLowerCase();
    for (let n = 1; n <= doc.numPages; n++) {
      if (textCache.current[n] === undefined) {
        try {
          const page = await doc.getPage(n);
          const tc = await page.getTextContent();
          textCache.current[n] = tc.items.map((i) => i.str).join(' ');
        } catch { textCache.current[n] = ''; }
      }
      const txt = textCache.current[n];
      const idx = txt.toLowerCase().indexOf(lower);
      if (idx >= 0) found.push({ num: n, snippet: txt.slice(Math.max(0, idx - 35), idx + 55) });
      if (n % 10 === 0) setMatches([...found]);
    }
    setMatches(found);
    setSearching(false);
  };

  const share = async () => {
    const url = `${window.location.origin}/annual-reports#report=${report.id}&page=${current}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied', { description: `Opens ${report.label} at page ${current}` });
    } catch {
      window.prompt('Copy this link:', url);
    }
  };

  const pageW = Math.round(BASE_W * (zoom / 100));
  const pageH = Math.round(pageW * pageRatio);

  const pages = useMemo(() => Array.from({ length: numPages }, (_, i) => i + 1), [numPages]);

  return (
    <div className="fixed inset-0 z-[100] bg-[#0A1628] flex flex-col" data-testid="report-reader">
      {/* Toolbar */}
      <div className="flex items-center gap-1.5 md:gap-3 px-3 md:px-5 py-2.5 bg-[#0D2444] text-white shadow-lg flex-wrap">
        <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10" data-testid="reader-close-btn" aria-label="Close reader">
          <X className="w-5 h-5" />
        </button>
        <div className="font-serif font-bold text-sm md:text-base mr-1">Annual Report {report.label}</div>
        <div className="text-xs bg-white/10 rounded-full px-3 py-1 font-mono" data-testid="reader-page-counter">
          {numPages ? `Page ${current} of ${numPages}` : 'Loading…'}
        </div>
        <div className="flex-1" />
        <button onClick={() => setPanel(panel === 'thumbs' ? null : 'thumbs')}
          className={`p-2 rounded-lg hover:bg-white/10 ${panel === 'thumbs' ? 'bg-white/15' : ''}`}
          title="Page thumbnails" data-testid="reader-thumbs-btn"><LayoutGrid className="w-5 h-5" /></button>
        <button onClick={() => setPanel(panel === 'search' ? null : 'search')}
          className={`p-2 rounded-lg hover:bg-white/10 ${panel === 'search' ? 'bg-white/15' : ''}`}
          title="Search this report" data-testid="reader-search-btn"><Search className="w-5 h-5" /></button>
        <div className="flex items-center gap-1 bg-white/10 rounded-full px-1 py-0.5" data-testid="reader-zoom-group">
          <ZoomIn className="w-4 h-4 ml-1.5 opacity-70" />
          {ZOOMS.map((z) => (
            <button key={z} onClick={() => setZoom(z)}
              className={`text-xs font-bold rounded-full px-2.5 py-1 ${zoom === z ? 'bg-[#D4AF37] text-[#0A1628]' : 'hover:bg-white/10'}`}
              data-testid={`reader-zoom-${z}`}>{z}%</button>
          ))}
        </div>
        <button onClick={share} className="p-2 rounded-lg hover:bg-white/10" title="Share this page" data-testid="reader-share-btn">
          <Share2 className="w-5 h-5" />
        </button>
        <a href={report.href} target="_blank" rel="noreferrer noopener"
          className="hidden md:inline-flex items-center gap-1.5 bg-[#D4AF37] text-[#0A1628] font-bold text-xs rounded-full px-3.5 py-1.5 hover:brightness-110"
          data-testid="reader-download-btn">
          <Download className="w-3.5 h-3.5" /> PDF · {report.size}
        </a>
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Side panel */}
        {panel && (
          <div className="w-72 md:w-80 bg-[#0F2440] text-white overflow-y-auto border-r border-white/10 flex-shrink-0" data-testid="reader-side-panel">
            {panel === 'thumbs' && (
              <div className="p-4 grid grid-cols-2 gap-3">
                {pages.map((n) => (
                  <button key={n} onClick={() => flipTo(n)}
                    className={`rounded-lg overflow-hidden text-left border-2 ${current === n ? 'border-[#D4AF37]' : 'border-transparent'}`}
                    data-testid={`thumb-${n}`}>
                    <div className="bg-white aspect-[1/1.35] flex items-center justify-center overflow-hidden">
                      {thumbs[n]
                        ? <img src={thumbs[n]} alt={`p${n}`} className="w-full h-full object-contain" />
                        : <Loader2 className="w-4 h-4 animate-spin text-gray-300" />}
                    </div>
                    <div className="text-center text-[10px] py-1 text-white/70 font-mono">{n}</div>
                  </button>
                ))}
              </div>
            )}
            {panel === 'search' && (
              <div className="p-4">
                <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-black mb-3">Search this report</div>
                <form onSubmit={(e) => { e.preventDefault(); runSearch(query.trim()); }} className="flex gap-2">
                  <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="e.g. volunteers"
                    className="flex-1 bg-white/10 rounded-lg px-3 py-2 text-sm placeholder-white/40 outline-none focus:ring-2 ring-[#D4AF37]"
                    data-testid="reader-search-input" />
                  <button type="submit" className="bg-[#D4AF37] text-[#0A1628] rounded-lg px-3 text-sm font-bold" data-testid="reader-search-submit">Go</button>
                </form>
                {searching && (
                  <div className="mt-4 flex items-center gap-2 text-white/60 text-xs">
                    <Loader2 className="w-4 h-4 animate-spin" /> Searching all {numPages} pages…
                  </div>
                )}
                {!searching && matches && (
                  <div className="mt-4 space-y-2" data-testid="reader-search-results">
                    <div className="text-xs text-white/60">{matches.length} matching page{matches.length === 1 ? '' : 's'} for “{activeQuery}”</div>
                    {matches.map((m) => (
                      <button key={m.num} onClick={() => flipTo(m.num)}
                        className="w-full text-left bg-white/5 hover:bg-white/10 rounded-lg p-3"
                        data-testid={`search-result-${m.num}`}>
                        <div className="text-xs font-bold text-[#D4AF37]">Page {m.num}</div>
                        <div className="text-[11px] text-white/70 mt-1">…{m.snippet}…</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Flipbook */}
        <div className="flex-1 overflow-auto flex items-center justify-center p-4 md:p-8" data-testid="reader-flipbook-area">
          {loadError ? (
            <div className="text-center text-white/80 max-w-md">
              <p className="font-bold mb-2">Could not load the PDF online.</p>
              <p className="text-sm text-white/60 mb-4">{loadError}</p>
              <a href={report.href} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A1628] font-bold rounded-full px-5 py-2.5 text-sm">
                <Download className="w-4 h-4" /> Download the PDF instead
              </a>
            </div>
          ) : !doc ? (
            <div className="text-center text-white/70">
              <Loader2 className="w-10 h-10 animate-spin mx-auto mb-3 text-[#D4AF37]" />
              <div className="text-sm">Opening Annual Report {report.label}…</div>
            </div>
          ) : (
            <HTMLFlipBook
              key={zoom}
              ref={bookRef}
              width={pageW}
              height={pageH}
              size="fixed"
              minWidth={220}
              maxWidth={1400}
              minHeight={300}
              maxHeight={1900}
              startPage={Math.max(0, current - 1)}
              showCover={true}
              usePortrait={true}
              mobileScrollSupport={true}
              maxShadowOpacity={0.5}
              flippingTime={700}
              onFlip={(e) => setCurrent(e.data + 1)}
              className="report-flipbook"
              style={{}}
            >
              {pages.map((n) => (
                <Page key={n} num={n} image={images[n]} />
              ))}
            </HTMLFlipBook>
          )}
        </div>
      </div>

      {/* Prev / next */}
      {doc && (
        <>
          <button onClick={() => flipTo(current - 1)}
            className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/25 text-white rounded-full p-3 backdrop-blur"
            data-testid="reader-prev-btn" aria-label="Previous page">
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button onClick={() => flipTo(current + 1)}
            className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/25 text-white rounded-full p-3 backdrop-blur"
            data-testid="reader-next-btn" aria-label="Next page">
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}
      <div className="text-center text-white/40 text-[11px] pb-2">Drag a page corner or use the arrows to flip · Download the PDF for the official copy</div>
    </div>
  );
};

export default PdfFlipReader;
